import React, { useState, useEffect, useRef } from 'react';
import './Contact.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faEnvelope, faCheck } from '@fortawesome/free-solid-svg-icons';
import ReCAPTCHA from 'react-google-recaptcha';

const ContactForm = () => {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState('');
  const [emailError, setEmailError] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Check if the URL has a form submission status parameter
  const [jsEnabled, setJsEnabled] = useState(true);
  const [recaptchaToken, setRecaptchaToken] = useState('');

  // Parse form submission status from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const submissionStatus = urlParams.get('submission');
    
    if (submissionStatus === 'success') {
      setStatus('E-Mail erfolgreich gesendet!');
    } else if (submissionStatus === 'error') {
      setStatus('E-Mail konnte nicht gesendet werden. Bitte versuchen Sie es später noch einmal.');
    }
    
    // Clean up URL after reading status
    if (submissionStatus) {
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Clear status message after 4 seconds
  useEffect(() => {
    if (status) {
      const timeout = setTimeout(() => {
        setStatus('');
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [status]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'email') {
      if (typingTimeout) clearTimeout(typingTimeout);
      setTypingTimeout(
        setTimeout(() => {
          validateEmail(value);
        }, 500)
      );
    }
  };

  const validateEmail = (email) => {
    if (email.trim() === '') {
      setEmailError('');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
    } else {
      setEmailError('');
    }
  };

  const handleRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailError) {
      setStatus('Bitte korrigieren Sie die Fehler im Formular.');
      return;
    }

    // Ensure reCAPTCHA is completed
    if (!recaptchaToken) {
      setStatus('Bitte verifizieren Sie, dass Sie kein Roboter sind.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData object from the form
      const formDataObj = new FormData(formRef.current);
      // Add the reCAPTCHA token
      formDataObj.append('g-recaptcha-response', recaptchaToken);
      formDataObj.append('js_enabled', 'true');

      // Submit to PHP endpoint using fetch API
      const response = await fetch('/process-form.php', {
        method: 'POST',
        body: formDataObj,
      });

      const result = await response.json();

      if (result.success) {
        setStatus('E-Mail erfolgreich gesendet!');
        // Reset form
        setFormData({
          name: '',
          surname: '',
          email: '',
          phone: '',
          message: '',
        });
        // Reset reCAPTCHA
        setRecaptchaToken('');
        if (window.grecaptcha) {
          window.grecaptcha.reset();
        }
      } else {
        setStatus(result.message || 'E-Mail konnte nicht gesendet werden.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('Ein Fehler ist beim Senden der Nachricht aufgetreten.');
      
      // If fetch fails, fall back to traditional form submission
      if (jsEnabled) {
        setJsEnabled(false);
        setTimeout(() => {
          formRef.current.submit();
        }, 100);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-container">
      <h1>Kontaktieren Sie Uns</h1>
      <form 
        ref={formRef}
        onSubmit={handleSubmit} 
        action="/process-form.php" 
        method="POST"
      >
        <div className="row">
          <div className="input-group">
            <input
              id="name"
              type="text"
              name="name"
              placeholder=" "
              value={formData.name}
              onChange={handleChange}
              required
            />
            <label htmlFor="name">Vorname</label>
          </div>
          <div className="input-group">
            <input
              id="surname"
              type="text"
              name="surname"
              placeholder=" "
              value={formData.surname}
              onChange={handleChange}
              required
            />
            <label htmlFor="surname">Nachname</label>
          </div>
        </div>
        <div className="row">
          <div className="input-group">
            <input
              id="email"
              type="email"
              name="email"
              placeholder=" "
              value={formData.email}
              onChange={handleChange}
              required
            />
            <label htmlFor="email">E-Mail Adresse</label>
            <div className={`error-message ${emailError ? 'visible' : ''}`}>
              <FontAwesomeIcon icon={faTriangleExclamation} /> {emailError}
            </div>
          </div>
          <div className="input-group">
            <input
              id="phone"
              type="tel"
              name="phone"
              placeholder=" "
              value={formData.phone}
              onChange={handleChange}
              required
            />
            <label htmlFor="phone">Telefon</label>
          </div>
        </div>
        <div className="input-group">
          <textarea
            id="message"
            name="message"
            rows="8"
            placeholder=" "
            value={formData.message}
            onChange={handleChange}
            required
          />
          <label htmlFor="message">Ihre Nachricht</label>
        </div>

        <div className="recaptcha-container">
          <ReCAPTCHA
            sitekey="6LdT72IrAAAAAGDv57kuF0UleSBpRehYJKUOK-vc"
            onChange={handleRecaptchaChange}
          />
        </div>
        
        {/* Hidden field for detecting non-JS submissions */}
        <input type="hidden" name="js_enabled" value="false" />
        
        <div className="button-area">
          <button
            type="submit"
            className={isSubmitting ? 'submitting' : ''}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faEnvelope} style={{ paddingRight: '5px' }} />
            {isSubmitting ? 'WIRD GESENDET...' : 'NACHRICHT SENDEN'}
          </button>
        </div>
        {status && (
          <div
            className={`status-message ${
              status.toLowerCase().includes('erfolgreich') ? 'success' : 'error'
            }`}
          >
            <FontAwesomeIcon
              icon={
                status.toLowerCase().includes('erfolgreich')
                  ? faCheck
                  : faTriangleExclamation
              }
            />
            {status}
            <div className="progress-bar"></div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
